import type { CspBucket } from '@shared/types';
import { fmtMoney, fmtPct } from '../../lib/format';
import { bucketColour } from '../../components/ui';

/**
 * One CSP bucket: a fill bar with the target band shaded behind it and a
 * midpoint tick. Mirrors the artifact's allocation rows.
 */
export function AllocationBar({
  label, bucket, value, pct, low, high,
}: {
  label: string; bucket: CspBucket; value: number; pct: number; low: number; high: number;
}) {
  const colour = bucketColour(bucket);
  const inRange = pct >= low && pct <= high;

  return (
    /*
     * Two lines per bucket rather than one crowded row: name + verdict on top,
     * the bar full-width underneath. Squeezing label, bar, two numbers and a
     * check into one 20px line is what made this card read as cramped — and
     * a bar that spans the whole card makes the target band actually visible.
     */
    <div style={{ margin: '14px 0' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 5, minWidth: 0 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: colour, flexShrink: 0, alignSelf: 'center' }} />
        <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {label}
        </span>
        <span className="vd-num" style={{ fontSize: 10.5, color: 'var(--vn-text-muted)' }}>
          target {low}{low !== high ? `–${high}` : ''}%
        </span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'baseline', gap: 8, whiteSpace: 'nowrap' }}>
          <span className="vd-num" style={{ fontSize: 13, fontWeight: 600, color: inRange ? 'var(--vn-success)' : 'var(--vn-text-primary)' }}>
            {fmtPct(pct)}{inRange ? ' ✓' : ''}
          </span>
          <span className="vd-num" style={{ fontSize: 11, color: 'var(--vn-text-muted)' }}>
            {fmtMoney(value)}/mo
          </span>
        </span>
      </div>

      <div style={{
        position: 'relative', height: 14, minWidth: 0,
        background: 'var(--vn-surface-inset)',
        border: '1px solid var(--vn-border-default)',
        borderRadius: 'var(--vn-radius-md)', overflow: 'hidden',
      }}>
        {/* acceptable range */}
        <div style={{
          position: 'absolute', left: `${low}%`, width: `${Math.max(1, high - low)}%`,
          top: 0, bottom: 0, background: 'rgba(255,255,255,0.07)',
        }} />
        {/* target midpoint — the dark tick */}
        <div style={{
          position: 'absolute', left: `${(low + high) / 2}%`, top: -3, bottom: -3,
          width: 2, background: 'var(--vn-text-primary)', opacity: 0.75, zIndex: 2,
        }} />
        {/* actual */}
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: `${Math.min(100, Math.max(0, pct))}%`,
          background: colour, opacity: 0.85, transition: 'width .35s ease', zIndex: 1,
        }} />
      </div>
    </div>
  );
}
